/**
 * InstitutionalVideoPlayer — reusable premium bilingual player
 * for the six flagship SHS institutional video packages.
 *
 * Behavior:
 *  * Fetches the video row by video_id from /api/shs-institutional-videos/{id}
 *  * Renders EN/ES toggle synced with the app-wide LanguageContext
 *  * Uses <video> when a rendered asset URL exists for the selected language
 *  * Otherwise renders a poster-and-captioned-script "production package"
 *    preview so the Founder can review scripts pre-render without fabrication
 *  * Never autoplays audio, always shows captions, respects prefers-reduced-motion
 *  * Lazy-loads the underlying <video>/poster (Intersection Observer + `preload="none"`)
 *
 * ZERO external deps beyond React + existing UI kit.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ChevronRight, ShieldCheck, AlertCircle, Eye } from 'lucide-react';
import { SecureVideo, ticketResolver } from './SecureVideo';
import { useEducationAuth } from '../contexts/EducationAuthContext';
import { useLanguage } from '../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const useReducedMotion = () => {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const on = () => setReduced(mq.matches);
    on();
    mq.addEventListener?.('change', on);
    return () => mq.removeEventListener?.('change', on);
  }, []);
  return reduced;
};

const InstitutionalVideoPlayer = ({
  videoId,
  language: langProp,          // 'en' | 'es' — overrides context if provided
  variant = 'default',         // 'default' | 'compact' | 'hero'
  onCtaClick = null,
  className = '',
  hideIfUnavailable = false,   // public placements: render nothing until PUBLISHED (Super Admin sees a preview)
  sectionClassName = null,     // wrap in a <section> with this className when rendered
}) => {
  const { language: siteLang } = useLanguage();
  const { user, token } = useEducationAuth();
  const isAdmin = user?.role === 'shs_admin' && !!token;
  const [row, setRow] = useState(null);
  const [portrait, setPortrait] = useState(null);
  const [error, setError] = useState(null);
  const [lang, setLang] = useState(langProp || siteLang || 'en');
  const [adminPoster, setAdminPoster] = useState({});
  const containerRef = useRef(null);
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    const l = langProp || siteLang;
    if (l === 'en' || l === 'es') setLang(l);
  }, [langProp, siteLang]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/shs-institutional-videos/${videoId}`);
        if (!r.ok) throw new Error(`video_fetch_${r.status}`);
        const d = await r.json();
        if (cancelled) return;
        setRow(d);
        // Portraits
        const p = await fetch(`${API_URL}/api/founder-portraits`);
        if (!p.ok) return;
        const pd = await p.json();
        const slot = d.primary_founder_portrait;
        if (!cancelled) setPortrait(pd?.portraits?.[slot] || null);
      } catch (e) {
        if (!cancelled) setError(String(e?.message || e));
      }
    })();
    return () => { cancelled = true; };
  }, [videoId]);

  const needsAdminPoster = isAdmin && row && !(row[`${lang}_asset`] || {}).poster_url && (row[`${lang}_asset`] || {}).has_thumbnail;
  useEffect(() => {
    if (!needsAdminPoster || adminPoster[lang]) return;
    fetch(`${API_URL}/api/admin/shs-institutional-videos/${videoId}/thumbnail/${lang}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.blob() : Promise.reject()))
      .then((b) => setAdminPoster((m) => ({ ...m, [lang]: URL.createObjectURL(b) })))
      .catch(() => {});
  }, [needsAdminPoster, lang, videoId, token, adminPoster]);

  if (error) {
    if (hideIfUnavailable) return null;
    return (
      <Card className={`bg-slate-900/60 border-slate-800 ${className}`} data-testid={`inst-video-error-${videoId}`}>
        <CardContent className="p-4 text-sm text-rose-300 flex items-center gap-2">
          <AlertCircle className="w-4 h-4" /> Video package unavailable ({error}).
        </CardContent>
      </Card>
    );
  }
  if (!row) {
    if (hideIfUnavailable) return null;
    return (
      <div className={`rounded-lg border border-slate-800 bg-slate-900/40 h-56 animate-pulse ${className}`} data-testid={`inst-video-loading-${videoId}`} />
    );
  }

  const title = row.title?.[lang] || row.title?.en;
  const script = row.script?.[lang] || row.script?.en || '';
  const cta = row.cta?.[lang] || row.cta?.en;
  const asset = (lang === 'es' ? row.es_asset : row.en_asset) || {};
  const publicUrl = asset.available && asset.video_url ? `${API_URL}${asset.video_url}` : null;
  const adminPreview = !publicUrl && isAdmin && asset.has_master;
  const posterUrl = (asset.poster_url ? `${API_URL}${asset.poster_url}` : adminPoster[lang]) || portrait?.url || null;
  if (hideIfUnavailable && !publicUrl && !adminPreview) return null;
  const paragraphs = script.split(/\n\n+/).filter(Boolean);
  const scriptSummary = paragraphs.slice(0, variant === 'hero' ? 5 : 3).join('\n\n');

  const handleCta = () => {
    if (onCtaClick) onCtaClick(cta);
    else if (cta?.route) window.location.href = cta.route;
  };

  const card = (
    <div
      ref={containerRef}
      className={`relative overflow-hidden rounded-xl border border-slate-800 bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900 ${className}`}
      data-testid={`inst-video-${videoId}`}
    >
      <div className="p-4 md:p-6 grid md:grid-cols-2 gap-4 md:gap-6 items-stretch">
        {/* Media panel */}
        <div className="relative self-start w-full rounded-lg overflow-hidden bg-slate-950 aspect-video" data-testid={`inst-video-media-${videoId}`}>
          {publicUrl || adminPreview ? (
            <>
              <SecureVideo
                key={`${videoId}-${lang}`}
                resolveSrc={publicUrl ? async () => publicUrl : ticketResolver(`/api/admin/shs-institutional-videos/${videoId}/playback-ticket?language=${lang}`, token)}
                poster={posterUrl || undefined}
                posterClassName="opacity-95"
                portrait={asset.orientation === 'portrait'}
                label={lang === 'es' ? 'Ver video' : 'Watch video'}
                className="!rounded-none"
                testId={`inst-video-player-${videoId}`}
              />
              {adminPreview && (
                <span className="absolute top-2 left-2 z-10 inline-flex items-center gap-1 rounded-full bg-amber-500/90 text-slate-950 text-[10px] font-semibold px-2 py-0.5 pointer-events-none" data-testid={`inst-video-admin-preview-${videoId}`}>
                  <Eye className="w-3 h-3" /> {lang === 'es' ? 'Vista previa del Fundador · no público' : 'Founder preview · not public'}
                </span>
              )}
            </>
          ) : (
            // Poster-and-package preview (no fabricated render)
            <div className="relative w-full h-full">
              {posterUrl ? (
                <img
                  src={posterUrl}
                  alt={portrait?.alt_en || 'SHS Founder portrait'}
                  className={`w-full h-full object-cover ${reducedMotion ? '' : 'transition-transform duration-[6000ms] hover:scale-105'}`}
                  loading="lazy"
                  data-testid={`inst-video-poster-${videoId}`}
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-blue-950 via-slate-900 to-orange-950/60" data-testid={`inst-video-poster-placeholder-${videoId}`} />
              )}
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/30 to-black/80" />
              <div className="absolute top-3 left-3 flex gap-2">
                <Badge className="bg-slate-950/70 text-slate-200 border-slate-700 text-[10px]">
                  {row.production_status === 'PRODUCTION_PACKAGE_READY' ? 'Production package · script + storyboard ready' : row.production_status}
                </Badge>
              </div>
              <div className="absolute bottom-3 left-3 right-3">
                <p className="text-white text-sm md:text-base font-semibold leading-snug">{title}</p>
                <p className="text-slate-300 text-xs mt-1">{Math.round((row.target_duration_seconds || 0) / 60)} min · EN / ES · captions ready</p>
              </div>
            </div>
          )}
        </div>

        {/* Copy panel */}
        <div className="flex flex-col">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-slate-100 font-bold text-lg md:text-xl leading-tight" data-testid={`inst-video-title-${videoId}`}>{title}</h3>
            <div className="ml-auto flex items-center gap-1 rounded-full bg-slate-800/70 px-1 py-0.5" data-testid={`inst-video-lang-toggle-${videoId}`}>
              <button
                onClick={() => setLang('en')}
                className={`text-[10px] px-2 py-0.5 rounded-full ${lang === 'en' ? 'bg-cyan-500/30 text-cyan-100' : 'text-slate-400 hover:text-slate-200'}`}
                data-testid={`inst-video-lang-en-${videoId}`}
              >EN</button>
              <button
                onClick={() => setLang('es')}
                className={`text-[10px] px-2 py-0.5 rounded-full ${lang === 'es' ? 'bg-cyan-500/30 text-cyan-100' : 'text-slate-400 hover:text-slate-200'}`}
                data-testid={`inst-video-lang-es-${videoId}`}
              >ES</button>
            </div>
          </div>
          {isAdmin && <p className="text-slate-400 text-[11px] mt-1">
            {asset.has_thumbnail ? (
              <>Approved thumbnail attached · not public until publication.</>
            ) : portrait?.configured ? (
              <>Founder portrait <span className="text-slate-300">{row.primary_founder_portrait}</span> configured.</>
            ) : (
              <>Founder portrait slot <span className="text-slate-300">{row.primary_founder_portrait}</span> — awaiting upload.</>
            )}
          </p>}
          <div className="mt-3 text-sm text-slate-300 whitespace-pre-line leading-relaxed max-h-64 md:max-h-72 overflow-y-auto pr-2" data-testid={`inst-video-script-${videoId}`}>
            {scriptSummary}
            {paragraphs.length > (variant === 'hero' ? 5 : 3) && (
              <p className="text-slate-500 italic mt-2">… full narration available in the rendered video.</p>
            )}
          </div>
          <div className="mt-4 flex items-center gap-2 flex-wrap">
            {cta?.label && (
              <Button onClick={handleCta} size="sm" className="bg-cyan-600 hover:bg-cyan-500 text-white" data-testid={`inst-video-cta-${videoId}`}>
                {cta.label} <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            )}
            <Badge className="bg-emerald-500/10 text-emerald-300 border-emerald-500/30 text-[10px]">
              <ShieldCheck className="w-3 h-3 mr-1" /> Clinical review: {row.clinical_review?.status || 'PENDING'}
            </Badge>
            {isAdmin && row.publication_status !== 'PUBLISHED' && (
              <Badge className="bg-amber-500/10 text-amber-300 border-amber-500/30 text-[10px]">
                Publication · {row.publication_status || 'HELD'}
              </Badge>
            )}
          </div>
        </div>
      </div>
    </div>
  );
  return sectionClassName ? (
    <section className={sectionClassName} data-testid={`inst-video-section-${videoId}`}>
      <div className="container mx-auto px-4 max-w-6xl">{card}</div>
    </section>
  ) : card;
};

export default InstitutionalVideoPlayer;
